import React, { FormEventHandler } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

const StyledSelect = styled.select<{ highlight?: boolean }>`
  min-height: 4rem;
  display: block;
  align-items: center;
  padding: 1.25rem 1rem;
  font-family: inherit;
  font-weight: ${props => props.theme.weights.regular};
  border-radius: 1rem;
  letter-spacing: 0.01em;
  border: none;
  box-sizing: border-box;
  font-size: 1.125rem;
  caret-color: transparent;
  border: 2px solid ${props => props.theme.colors.formFieldBackground};
  background-color: ${props => props.theme.colors.formFieldBackground};
  appearance: none;
  width: 100%;
  padding-right: 3rem;
  color: ${props => props.theme.colors.label};

  &::placeholder {
    color: ${props => props.theme.colors.disabledLabel};
  }

  &:focus {
    border: 2px solid ${props => props.theme.colors.inputFocusBorder};
    background: ${props => props.theme.colors.background};
    outline: none;
  }

  &:disabled {
    color: ${props => props.theme.colors.disabledLabel};
    background: ${props => props.theme.colors.secondaryBackground};
    border-color: ${props => props.theme.colors.secondaryBackground};
  }
  ${props =>
    props.highlight &&
    css`
      background-color: ${props.theme.colors.bannerBackgroundSuccess};
      border-color: ${props.theme.colors.inputFocusBorder};
    `}
`
const SelectContainer = styled.div<{ invalid?: boolean; isPlaceholder?: boolean }>`
  position: relative;
  svg {
    position: absolute;
    top: 1.25rem;
    right: 1rem;
    pointer-events: none;
  }
  :focus-within {
    color: ${props => props.theme.colors.primary};
  }
  ${({ isPlaceholder, theme }) =>
    isPlaceholder &&
    `select {
        color: ${theme.colors.disabledLabel};
    }`}
  ${({ invalid, theme }) =>
    invalid &&
    css`
      & select,
      & select:focus {
        border-color: ${theme.colors.error};
      }
      & svg {
        color: ${theme.colors.error};
      }
    `}
`
export type SelectOption = {
  label: string
  jumpstart?: boolean
} & Partial<React.HTMLProps<HTMLOptionElement>>
export const Caret = () => {
  return (
    <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18 10l-6 6-6-6"
        stroke={'currentColor'}
        strokeWidth="3"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const mapOptions = (options: SelectOption[], value: string | undefined, placeholderValue: string | number) => (
  <>
    {options.map(({ label, ...rest }) => (
      <option
        {...rest}
        key={rest.value?.toString()}
        selected={
          value?.toString() === rest.value?.toString() && rest.value?.toString() !== placeholderValue.toString()
        }
      >
        {label}
      </option>
    ))}
  </>
)
export type SelectProps = {
  options: SelectOption[]
  invalid?: boolean
  placeholder?: string
  placeholderValue?: string | number
  value?: string
  defaultValue?: string
  allowCustomValue?: boolean
  highlight?: boolean
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void | FormEventHandler<HTMLSelectElement>
} & Pick<React.HTMLProps<HTMLSelectElement>, 'style' | 'disabled' | 'name' | 'className' | 'autoComplete'>

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options,
      disabled,
      placeholder,
      onChange,
      value,
      defaultValue,
      invalid,
      className,
      allowCustomValue,
      placeholderValue = '',
      ...props
    },
    ref
  ) => {
    const [selected, setSelected] = React.useState(value)
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelected(e.target.value)
      if (onChange) {
        onChange(e)
      }
    }
    React.useEffect(() => {
      setSelected(value)
    }, [value])
    const isPlaceholderValue = (selected == null || selected === placeholderValue) && placeholder != null
    return (
      <SelectContainer className={className} invalid={invalid} isPlaceholder={isPlaceholderValue}>
        <Caret />
        <StyledSelect ref={ref} disabled={disabled} onChange={handleChange} {...props}>
          {placeholder != null && (
            <option value={placeholderValue} disabled hidden selected={isPlaceholderValue}>
              {placeholder}
            </option>
          )}
          {mapOptions(options, selected, placeholderValue)}
          {allowCustomValue && !options.find(item => item.value === value) && value != null && value.trim() !== '' && (
            <option value={value} selected={true}>
              {value}
            </option>
          )}
          {/* dirty hack to fix truncation of long options on iOS https://stackoverflow.com/questions/19398154/how-to-fix-truncated-text-on-select-element-on-ios7 -- tgraham 10.10.17 */}
          <optgroup disabled />
        </StyledSelect>
      </SelectContainer>
    )
  }
)

export default Select
